import { useEffect, useRef, useState } from 'react';

const services = [
  {
    title: 'New Construction & Remodeling',
    desc: 'Ground-up builds & full additions. We sell the contract — you deliver the masterpiece.',
    icon: 'ri-building-line',
    image: 'https://public.readdy.ai/ai/img_res/3f5ab8f20bd84772275350248a5b3b4f.jpg',
    accent: '#DC5014',
  },
  {
    title: 'Epoxy Flooring',
    desc: 'High-durability coatings for luxury garages and commercial spaces. Premium finish, elite clients.',
    icon: 'ri-layout-grid-line',
    image: 'https://readdy.ai/api/search-image?query=Stunning%20luxury%20epoxy%20flooring%20garage%20showroom%20metallic%20high%20gloss%20finish%20reflective%20surface%20premium%20coating%20modern%20clean%20bright%20interior%20professional%20photography%20wide%20shot%20upscale%20residential%20garage&width=600&height=400&seq=svc-epoxy-01&orientation=landscape',
    accent: '#143C3C',
  },
  {
    title: 'Roofing, Solar & Battery',
    desc: 'Complete energy & protection systems. From premium roofing to full solar and battery backup.',
    icon: 'ri-sun-line',
    image: 'https://readdy.ai/api/search-image?query=Modern%20luxury%20home%20rooftop%20solar%20panels%20installation%20premium%20residential%20property%20Florida%20blue%20sky%20bright%20sunlight%20clean%20energy%20system%20professional%20aerial%20photography%20upscale%20neighborhood&width=600&height=400&seq=svc-solar-01&orientation=landscape',
    accent: '#DC5014',
  },
  {
    title: 'Kitchen & Bathroom Design',
    desc: 'Luxury transformations & custom cabinetry. Where high-ticket clients make their decisions.',
    icon: 'ri-home-heart-line',
    image: 'https://readdy.ai/api/search-image?query=Ultra%20luxury%20kitchen%20remodel%20custom%20white%20cabinetry%20marble%20countertops%20gold%20fixtures%20professional%20interior%20design%20photography%20bright%20natural%20light%20high%20end%20appliances%20modern%20elegant%20residential%20kitchen&width=600&height=400&seq=svc-kitchen-01&orientation=landscape',
    accent: '#143C3C',
  },
  {
    title: 'Pool & Outdoor Living',
    desc: 'Custom pools, outdoor kitchens, and hardscaping. The ultimate luxury lifestyle upgrade.',
    icon: 'ri-water-flash-line',
    image: 'https://readdy.ai/api/search-image?query=Stunning%20luxury%20custom%20swimming%20pool%20outdoor%20living%20area%20Florida%20resort%20style%20infinity%20pool%20outdoor%20kitchen%20pergola%20lush%20tropical%20landscaping%20golden%20hour%20sunset%20professional%20photography%20upscale%20residential&width=600&height=400&seq=svc-pool-01&orientation=landscape',
    accent: '#DC5014',
  },
  {
    title: 'Driveway & Turf',
    desc: 'Premium pavers and artificial grass. First impressions that close deals before you say a word.',
    icon: 'ri-road-map-line',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20driveway%20premium%20pavers%20artificial%20turf%20grass%20modern%20residential%20home%20Florida%20curb%20appeal%20professional%20landscaping%20clean%20lines%20elegant%20entrance%20upscale%20neighborhood%20bright%20daylight%20photography&width=600&height=400&seq=svc-driveway-01&orientation=landscape',
    accent: '#143C3C',
  },
  {
    title: 'Home Automation',
    desc: 'Smart systems and security. The technology layer that defines a true luxury property.',
    icon: 'ri-smartphone-line',
    image: 'https://readdy.ai/api/search-image?query=Modern%20smart%20home%20automation%20system%20luxury%20interior%20control%20panel%20tablet%20smart%20lighting%20security%20cameras%20premium%20residential%20technology%20elegant%20living%20room%20professional%20photography%20upscale%20home&width=600&height=400&seq=svc-automation-01&orientation=landscape',
    accent: '#DC5014',
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={ref}
      className="px-5 sm:px-8 md:px-[60px]"
      style={{ background: '#F8F8F8', paddingTop: '80px', paddingBottom: '80px' }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          className="text-center mb-14 md:mb-20"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div style={{ width: '32px', height: '1px', background: '#DC5014' }} />
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '11px',
                color: '#DC5014',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
              }}
            >
              SERVICES WE SELL
            </span>
            <div style={{ width: '32px', height: '1px', background: '#DC5014' }} />
          </div>

          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(28px, 6vw, 52px)',
              fontWeight: 700,
              color: '#0A0A0A',
              lineHeight: 1.15,
              marginBottom: '20px',
            }}
          >
            We sell the elite contract.
            <br />
            <span style={{ color: '#DC5014', fontStyle: 'italic' }}>You execute with mastery.</span>
          </h2>

          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '16px',
              color: 'rgba(10,10,10,0.5)',
              maxWidth: '540px',
              margin: '0 auto',
              lineHeight: 1.8,
            }}
          >
            Our commercial team closes $100k+ contracts across the categories below. You step in as the elite executor — with the full Barkfy infrastructure behind you.
          </p>
        </div>

        {/* Services Grid — 1 col mobile, 2 col sm, 3 col lg */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
          {services.slice(0, 6).map((service, i) => (
            <div
              key={service.title}
              style={{
                background: '#FFFFFF',
                border: '1px solid rgba(20,60,60,0.08)',
                borderRadius: '8px',
                overflow: 'hidden',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.7s ease ${i * 80}ms, transform 0.7s ease ${i * 80}ms`,
                cursor: 'default',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-6px)';
                (e.currentTarget as HTMLDivElement).style.borderColor = service.accent === '#DC5014' ? 'rgba(220,80,20,0.3)' : 'rgba(20,60,60,0.3)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = visible ? 'translateY(0)' : 'translateY(32px)';
                (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(20,60,60,0.08)';
              }}
            >
              {/* Image */}
              <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top"
                  style={{ transition: 'transform 0.5s ease' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.05)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'; }}
                />
              </div>

              {/* Content */}
              <div style={{ padding: '20px 24px' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{
                      width: '36px',
                      height: '36px',
                      background: service.accent === '#DC5014' ? 'rgba(220,80,20,0.1)' : 'rgba(20,60,60,0.08)',
                    }}
                  >
                    <i className={service.icon} style={{ color: service.accent, fontSize: '18px' }} />
                  </div>
                  <h3
                    style={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      fontSize: '16px',
                      color: '#0A0A0A',
                      lineHeight: 1.3,
                    }}
                  >
                    {service.title}
                  </h3>
                </div>
                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 300,
                    fontSize: '14px',
                    color: 'rgba(10,10,10,0.55)',
                    lineHeight: 1.7,
                  }}
                >
                  {service.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 7th card — full width centered */}
        <div className="flex justify-center">
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid rgba(20,60,60,0.08)',
              borderRadius: '8px',
              overflow: 'hidden',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease 480ms, transform 0.7s ease 480ms',
              cursor: 'default',
              maxWidth: '480px',
              width: '100%',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-6px)';
              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(220,80,20,0.3)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.transform = visible ? 'translateY(0)' : 'translateY(32px)';
              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(20,60,60,0.08)';
            }}
          >
            <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
              <img
                src={services[6].image}
                alt={services[6].title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ width: '36px', height: '36px', background: 'rgba(220,80,20,0.1)' }}
                >
                  <i className={services[6].icon} style={{ color: '#DC5014', fontSize: '18px' }} />
                </div>
                <h3
                  style={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#0A0A0A',
                    lineHeight: 1.3,
                  }}
                >
                  {services[6].title}
                </h3>
              </div>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 300,
                  fontSize: '14px',
                  color: 'rgba(10,10,10,0.55)',
                  lineHeight: 1.7,
                }}
              >
                {services[6].desc}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="text-center mt-14 md:mt-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 560ms, transform 0.7s ease 560ms',
          }}
        >
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-sans font-bold whitespace-nowrap cursor-pointer transition-all duration-300 hover:brightness-110 active:scale-95"
            style={{
              background: '#DC5014',
              color: '#FFFFFF',
              padding: '0 52px',
              height: '52px',
              minHeight: '52px',
              borderRadius: '6px',
              fontSize: '13px',
              letterSpacing: '0.12em',
              border: 'none',
            }}
          >
            APPLY TO JOIN THE ELITE HUB
          </button>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontStyle: 'italic',
              fontSize: '12px',
              color: 'rgba(10,10,10,0.35)',
              marginTop: '12px',
            }}
          >
            Limited availability: Only 3 spots left this quarter.
          </p>
        </div>

      </div>
    </section>
  );
}
